// src/components/research/ResearchProjectList.jsx
// Übersicht (Menü) aller Research-Projekte — analog zur Dossier-Liste im
// Kostentool. Klick auf die Navigation "Research" landet hier; wurde zuvor ein
// Projekt geöffnet, leitet die Liste automatisch dorthin weiter (Auto-Open).
// Der Zurück-Button im Projekt navigiert mit state.showList=true hierher und
// unterdrückt damit das Auto-Open.

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { canAccess, canEdit } from "../../utils/permissions";
import {
  getAllResearchProjects,
  createResearchProject,
  updateResearchProject,
  softDeleteResearchProject,
  getLastResearchProjectId,
  setLastResearchProjectId,
} from "../../services/azureResearchProjectsService";

const ResearchProjectList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const canManage = canEdit(user, "research");

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal "Neues Research-Projekt". Quellen: neue Projekte nutzen automatisch
  // die gemeinsame Tabelle researchSources (Partition = Projekt-ID) — bewusst
  // kein Eingabefeld dafür; abweichende Tabellen nur programmatisch.
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [formError, setFormError] = useState("");

  // Rechtsklick-Kontextmenü + Inline-Umbenennen (Muster wie EstimateList)
  const [contextMenu, setContextMenu] = useState(null); // { x, y, project }
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const renameInputRef = useRef(null);

  // Zurück-Button im Projekt setzt state.showList -> Liste anzeigen statt Auto-Open.
  const suppressAutoOpen = location.state?.showList === true;
  const autoOpenTriedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await getAllResearchProjects();
        if (!cancelled) setProjects(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-Open: letztgeöffnetes Projekt direkt wieder öffnen (einmal pro Mount).
  useEffect(() => {
    if (loading || suppressAutoOpen || autoOpenTriedRef.current) return;
    autoOpenTriedRef.current = true;
    const lastId = getLastResearchProjectId();
    if (lastId && projects.some((p) => p.id === lastId)) {
      navigate(`/main-research/${lastId}`, { replace: true });
    } else if (lastId) {
      // Projekt existiert nicht mehr (gelöscht) -> Merker aufräumen.
      setLastResearchProjectId("");
    }
  }, [loading, projects, suppressAutoOpen, navigate]);

  const filteredProjects = projects.filter((p) => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return p.name.toLowerCase().includes(t) || p.description.toLowerCase().includes(t);
  });

  const openProject = (project) => {
    if (editingProjectId === project.id) return;
    navigate(`/main-research/${project.id}`);
  };

  const handleCreate = async () => {
    const name = formData.name.trim();
    if (!name) {
      setFormError("Name ist erforderlich");
      return;
    }
    try {
      setSaving(true);
      setFormError("");
      const created = await createResearchProject({
        name,
        description: formData.description.trim(),
        createdBy: user?.userDetails || user?.email || user?.name || "",
      });
      setShowModal(false);
      setFormData({ name: "", description: "" });
      navigate(`/main-research/${created.id}`);
    } catch (err) {
      setFormError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e, project) => {
    e.stopPropagation();
    if (
      !window.confirm(
        `Research-Projekt "${project.name}" wirklich löschen?\n\nDie Einträge bleiben in der Datenbank erhalten (Soft-Delete).`
      )
    )
      return;
    try {
      await softDeleteResearchProject(project.id);
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
      if (getLastResearchProjectId() === project.id) setLastResearchProjectId("");
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  // === Rechtsklick-Kontextmenü ===
  const handleContextMenu = (e, project) => {
    if (!canManage) return;
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, project });
  };

  const startRename = (project) => {
    setContextMenu(null);
    setEditingProjectId(project.id);
    setEditingName(project.name || "");
  };

  const cancelRename = () => {
    setEditingProjectId(null);
    setEditingName("");
  };

  const saveRename = async () => {
    if (!editingProjectId) return;
    const trimmed = editingName.trim();
    const current = projects.find((p) => p.id === editingProjectId);
    if (!current || !trimmed || trimmed === current.name) {
      cancelRename();
      return;
    }
    setRenameSaving(true);
    try {
      await updateResearchProject(editingProjectId, { name: trimmed });
      setProjects((prev) =>
        prev.map((p) => (p.id === editingProjectId ? { ...p, name: trimmed } : p))
      );
      cancelRename();
    } catch (err) {
      console.error("Rename error:", err);
      // Feld bleibt offen, damit erneut versucht werden kann.
    } finally {
      setRenameSaving(false);
    }
  };

  // Kontextmenü schliessen bei Klick/Scroll/Escape ausserhalb.
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    const handleKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("scroll", close, true);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", close, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, [contextMenu]);

  // Fokus auf Input bei Rename-Start.
  useEffect(() => {
    if (editingProjectId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [editingProjectId]);

  if (authLoading) {
    return <div className="loading">Wird geladen…</div>;
  }

  if (!user || !canAccess(user, "research")) {
    return (
      <div className="access-denied">
        <h3>Zugriff verweigert</h3>
        <p>Sie haben keine Berechtigung für diesen Bereich.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Research-Projekte werden geladen…</div>;
  }

  return (
    <div className="research-project-list">
      <div className="team-controls">
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Research-Projekt suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="table-info">
          {filteredProjects.length} {filteredProjects.length === 1 ? "Projekt" : "Projekte"}
        </div>
        {canManage && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            Neues Research-Projekt
          </button>
        )}
      </div>

      <div className="team-table-container">
        <table className="team-table team-table-compact">
          <thead>
            <tr>
              <th>Name</th>
              <th>Beschreibung</th>
              <th>Zuletzt geändert</th>
              <th>Erstellt von</th>
              {canManage && <th></th>}
            </tr>
          </thead>
          <tbody>
            {filteredProjects.length === 0 ? (
              <tr>
                <td
                  colSpan={canManage ? 5 : 4}
                  style={{ textAlign: "center", padding: "2rem", opacity: 0.6 }}
                >
                  {searchTerm
                    ? "Keine Research-Projekte gefunden."
                    : "Noch keine Research-Projekte vorhanden."}
                </td>
              </tr>
            ) : (
              filteredProjects.map((p) => {
                const isEditing = editingProjectId === p.id;
                return (
                  <tr
                    key={p.id}
                    className="team-row clickable-row"
                    onClick={() => openProject(p)}
                    onContextMenu={(e) => handleContextMenu(e, p)}
                  >
                    <td className="cell-name" onClick={(e) => { if (isEditing) e.stopPropagation(); }}>
                      {isEditing ? (
                        <input
                          ref={renameInputRef}
                          type="text"
                          className="cell-name-input"
                          value={editingName}
                          disabled={renameSaving}
                          onChange={(e) => setEditingName(e.target.value)}
                          onBlur={saveRename}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              saveRename();
                            } else if (e.key === "Escape") {
                              e.preventDefault();
                              cancelRename();
                            }
                          }}
                        />
                      ) : (
                        p.name
                      )}
                    </td>
                    <td className="cell-description">{p.description}</td>
                    <td>
                      {p.updatedAt ? new Date(p.updatedAt).toLocaleDateString("de-CH") : ""}
                    </td>
                    <td>{p.createdBy}</td>
                    {canManage && (
                      <td className="cell-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="action-btn action-btn-danger"
                          onClick={(e) => handleDelete(e, p)}
                          title="Research-Projekt löschen"
                        >
                          &times;
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Rechtsklick-Kontextmenü */}
      {contextMenu && (
        <div
          className="context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="context-menu-header" title={contextMenu.project.name}>
            {contextMenu.project.name}
          </div>
          <button
            type="button"
            className="context-menu-item"
            onClick={() => startRename(contextMenu.project)}
          >
            Umbenennen
          </button>
        </div>
      )}

      {/* Modal: Neues Research-Projekt */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content estimate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Neues Research-Projekt</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              {formError && <div className="error-message">{formError}</div>}
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Sakralbauten (Buch)"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Beschreibung</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Worum geht es in diesem Research-Projekt?"
                />
                <small className="form-hint">
                  Quellen (Bookmarks der Plugins) landen automatisch in der gemeinsamen
                  Tabelle researchSources, Dateien im Projekt-Ordner von research-files.
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={saving}
              >
                Abbrechen
              </button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
                {saving ? "Speichern..." : "Projekt erstellen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearchProjectList;
